import { useQuery } from "@tanstack/react-query";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { APITokenSection } from "@/components/organization/APITokenSection";
import { APIEndpointSection } from "@/components/organization/APIEndpointSection";
import { EnvironmentCredentialsCard } from "@/components/organization/EnvironmentCredentialsCard";

export default function ApiConfiguration() {
  const { user } = useAuth();

  const { data: userRole } = useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase.rpc("get_user_role", { user_id: user.id });
      return data as string | null;
    },
    enabled: !!user?.id,
  });
  const isSuperuser = userRole === "superuser";

  const { data: userOrganization } = useQuery({
    queryKey: ["user-organization", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("user_roles")
        .select("organization_id")
        .eq("user_id", user.id)
        .maybeSingle();
      return data?.organization_id || null;
    },
    enabled: !!user?.id,
  });

  const { data: organization, isLoading } = useQuery({
    queryKey: ["organization-details", userOrganization],
    queryFn: async () => {
      if (!userOrganization) return null;
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("id", userOrganization)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userOrganization,
  });

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader {...PAGE_HEADERS.apiConfiguration} />
          <div className="p-8 space-y-12" style={{ paddingTop: "8.5rem" }}>
            {isLoading && (
              <p className="text-muted-foreground">Loading API configuration...</p>
            )}
            {organization && (
              <>
                <APITokenSection organizationId={organization.id} />
                <APIEndpointSection organizationId={organization.id} />
                <EnvironmentCredentialsCard
                  organizationId={organization.id}
                  environment="development"
                  title="Development Environment"
                  description="Sandbox credentials for building and iterating against Snapshot APIs without touching production devices."
                  canEdit={false}
                />
                <EnvironmentCredentialsCard
                  organizationId={organization.id}
                  environment="test"
                  title="Test Environment"
                  description="Pre-production credentials for end-to-end integration testing before promoting changes live."
                  canEdit={false}
                />
                {isSuperuser && (
                  <p className="text-sm text-muted-foreground">
                    Manage per-customer Development and Test credentials from CRM → Organisation → API Environments.
                  </p>
                )}
              </>
            )}
            {!isLoading && !organization && (
              <p className="text-muted-foreground">
                No organisation found for this user.
              </p>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}